import { boundarySource } from '../boundary-journal.js';
// janus-engine/ability/charges/mastery-marker.ts — Wizard Mastery port (2026-10-09, owner: "port these, never re-open the PHP fallback").
// The "Holding Mastery" MARKER TOUCH: mastery_upkeep.php ft_touch_mastery_marker(), called from adjust_charges() on every non-zero Mastery
// request (gain OR loss, gated on the REQUESTED delta, not the clamped move — "ties go to the player"). The end-of-turn upkeep drains a
// pool whose marker was stamped before the current campaign time, so every Mastery change re-stamps it:
//   · no campaign clock          → nothing (no turns to police);
//   · no marker on the holder    → apply_status_effect(Holding Mastery, permanent, its OWN connection, no log row) — the status lane's
//                                  applyStatusEffect, gain arms included, exactly as PHP's apply runs them;
//   · a marker already held      → PHP re-stamps status_starts_campaign_* in place (update_status_effect_stats — no triggers, no log).
//                                  The ability commit persists status rows as adds / removes, so the re-stamp is the stored row REMOVED
//                                  and an identical row ADDED (same caster, connection, origin, end, flags); the commit stamps the new
//                                  row's start from the campaign clock — the value PHP writes. A stamp already equal to now (or a marker
//                                  this use added) is left as is: PHP's write would change nothing.
// The commit only persists; the page decided (CLAUDE.md "game logic is front end").
import { type JanusCtx, type Row, type StatusRow, activeStatuses, ecId, handBack, pendingStatusDefinitions, record, upper } from '../context.js';
import { type JanusInput } from '../janus.js';
import { applyStatusEffect } from '../statuses/apply-status-effect.js';
import { removeStatusFromRow, statusCasterId, statusItemId } from '../../statuses/expiry.js';

/** ClassMechanic 'mastery' (Wizard) — ft_mastery_pool_item_id(). */
export const MASTERY_POOL = 'B56929A7-6BB8-81FA-BFF8-2CE4B0812030';
/** StatusEffect 'holding_mastery' — ft_holding_mastery_status_item_id() (statuses/expiry.ts STATUS_IDS.holding_mastery). */
export const HOLDING_MASTERY = 'EEDD357F-C1CA-44C1-A53F-DD06EEFBA597';
const UNITS = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
/** ft_mastery_marker_suppressed(): a touch never re-enters through the gain arms its own apply fires. */
const touching = new WeakSet<JanusCtx>();
const suppressed = new WeakSet<JanusCtx>();

/** Upkeep spends the pool without stamping the marker it is retiring. */
export function withMasteryMarkerSuppressed<T>(ctx: JanusCtx, work: () => T): T {
  const already = suppressed.has(boundarySource(ctx));
  suppressed.add(boundarySource(ctx));
  try { return work(); } finally { if (!already) suppressed.delete(boundarySource(ctx)); }
}

/** The caller must persist full marker adds/removals (ability/statusStacks or turn/masteryMarkers)
 * and hold the marker definition. Other frames refuse before drawing. */
export function masteryMarkerPreDraw(ctx: JanusCtx, holder?: Row): void {
  if (suppressed.has(boundarySource(ctx))) return;
  if (ctx.statusStacks === undefined && !ctx.masteryMarkers) handBack('adjust_charges:mastery-marker');
  if (!ctx.read.campaignClock()) return;
  if (!ctx.read.definition(HOLDING_MASTERY)) handBack(`adjust_charges:mastery-marker:definition-not-held:${HOLDING_MASTERY}`);
  if (holder) heldMarker(ctx, holder);
}

/** The holder's one marker row (null = none). Two rows, or a stored row without its id, is a named hand-back — asked pre-draw too (a
 *  `holder` passed above), since a dry run need not reach the firing dispatch. */
function heldMarker(ctx: JanusCtx, holder: Row): Row | null {
  const holderId = ecId(holder);
  if (!holderId) handBack('adjust_charges:holder-not-held');
  const held = activeStatuses(ctx, ctx.members.get(holderId) ?? holder).filter(status => statusItemId(status) === HOLDING_MASTERY);
  if (held.length > 1) handBack('adjust_charges:mastery-marker:ambiguous-row'); // ft_find_holding_mastery_status_row() takes the first
  const row = held[0] ?? null;
  if (row && !ctx.statusesAdded.includes(row as unknown as StatusRow)) {
    if (!UUID.test(upper(field(row, 'status_id')))) handBack('adjust_charges:mastery-marker:row-id-not-held');
    // a re-stamp re-writes the row as remove + identical add: only a PLAIN marker row is carried over exactly (its caster a member of this
    // encounter — the commit requires a member caster, so an empty or foreign caster cannot be carried over unchanged; no area anchor, no stored selection)
    const caster = statusCasterId(row);
    const selection = field(row, 'status_casting_targets_arr');
    if (!ctx.members.has(caster) || int(field(row, 'status_aoe_source')) !== 0 || upper(field(row, 'status_aoe_type')) !== ''
        || (selection != null && selection !== ''))
      handBack('adjust_charges:mastery-marker:restamp-row-shape');
  }
  return row;
}

const field = (row: Row, key: string): unknown => row[key] ?? record(row.statuses)?.[key];
const int = (value: unknown): number => { const n = Number(value); return Number.isFinite(n) ? Math.trunc(n) : 0; };

/** ft_touch_mastery_marker() on `holder` (the adjust_charges caster). */
export function touchMasteryMarker(ctx: JanusCtx, holder: Row): void {
  if (touching.has(boundarySource(ctx)) || suppressed.has(boundarySource(ctx))) return;
  masteryMarkerPreDraw(ctx);
  const now = ctx.read.campaignClock();
  if (!now) return;
  const holderId = ecId(holder);
  const member = ctx.members.get(holderId) ?? holder;
  const row = heldMarker(ctx, holder);
  touching.add(boundarySource(ctx));
  try {
    if (!row) {
      const definition = ctx.read.definition(HOLDING_MASTERY)!;
      const input = { casterId: holderId, targetIds: [holderId], abilityId: HOLDING_MASTERY, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
        useAbilityVariable1: null, useAbilityVariable2: null } satisfies JanusInput;
      applyStatusEffect(ctx, input, definition, HOLDING_MASTERY, 'status_effect_applied', member, member,
        { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, false);
      return;
    }
    // a marker THIS use added was stamped with the commit's clock already
    if (ctx.statusesAdded.includes(row as unknown as StatusRow)) return;
    const stamp = UNITS.map(u => int(field(row, `status_starts_campaign_${u}`)));
    if (UNITS.every((u, i) => stamp[i] === now[u])) return;
    const statusId = upper(field(row, 'status_id'));
    const caster = statusCasterId(row);
    const connection = upper(field(row, 'status_connected_id'));
    const origin = upper(field(row, 'status_origin_id'));
    const damageType = upper(field(row, 'status_damage_type'));
    const forced = int(field(row, 'status_forced_roll_extreme'));
    for (const m of ctx.members.values()) removeStatusFromRow(m, statusId);
    ctx.statusesRemoved.push({ status_item_id: HOLDING_MASTERY, target: holderId, status_id: statusId });
    ctx.statusesAdded.push({
      // heldMarker() proved the row plain (a member caster, no area / stored selection) — every other column is carried over
      status_item_id: HOLDING_MASTERY, target: holderId, caster,
      status_connected_id: UUID.test(connection) ? connection : ctx.read.uuid(), status_stack: Math.max(1, int(field(row, 'status_stack')) || 1),
      status_ends_on_target_start_of_turn: int(field(row, 'status_ends_on_target_start_of_turn')) ? 1 : 0,
      status_ends_on_target_end_of_turn: int(field(row, 'status_ends_on_target_end_of_turn')) ? 1 : 0,
      status_ends_on_caster_start_of_next_turn: int(field(row, 'status_ends_on_caster_start_of_next_turn')) ? 1 : 0,
      status_ends_on_caster_end_of_next_turn: int(field(row, 'status_ends_on_caster_end_of_next_turn')) ? 1 : 0,
      ends: UNITS.map(u => Math.max(0, int(field(row, `status_ends_campaign_${u}`)))).join('-'),
      status_aoe_type: null, status_aoe_distance: 0, status_aoe_distance_depth: 0, status_aoe_distance_height: 0, status_aoe_source: 0,
      status_origin_point_x: 0, status_origin_point_y: 0, status_origin_point_z: 0,
      status_distance_point_x: 0, status_distance_point_y: 0, status_distance_point_z: 0,
      status_origin_id: UUID.test(origin) ? origin : null, status_casting_targets_arr: null,
      status_opposing_team_only: Math.min(2, Math.max(0, int(field(row, 'status_opposing_team_only')))), status_damage_type: UUID.test(damageType) ? damageType : null,
      status_forced_roll_extreme: forced > 0 ? 1 : forced < 0 ? -1 : 0,
    });
    pendingStatusDefinitions.set(ctx.statusesAdded[ctx.statusesAdded.length - 1]!, ctx.read.definition(HOLDING_MASTERY)!);
  } finally { touching.delete(boundarySource(ctx)); }
}
